import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Field, Section, Toggle } from '@/components/ui/editor-fields';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { post } from '@/lib/api/client';

/**
 * Create a store.
 *
 * A sidebar action rather than a page, as it was in the original — it is a form
 * you fill in once and never come back to, so it has no URL of its own.
 *
 * `businesses/new` is one of the few endpoints that checks a role for itself
 * (SuperAdmin, CustomerSupportAdmin or StoreAdmin). The sidebar entry sits
 * under Admin Services, which is narrower still.
 */

interface NewBusiness {
  ownerEmail: string;
  name: string;
  description: string;
  address1: string;
  address2: string;
  city: string;
  lat: number;
  lon: number;
  isBeta: boolean;
  allowPickup: boolean;
  minimumPickupOrderAmount: number;
  allowDelivery: boolean;
  minimumDeliveryOrderAmount: number;
  hasDeli: boolean;
  acceptsEBT: boolean;
  pricesIncludeTax: boolean;
}

function empty(): NewBusiness {
  return {
    ownerEmail: '',
    name: '',
    description: '',
    address1: '',
    address2: '',
    city: '',
    lat: 0,
    lon: 0,
    // A new store starts in beta: it has no catalogue, no staff and no hours
    // yet, and the original's default of false put it in front of shoppers the
    // moment it was created.
    isBeta: true,
    allowPickup: false,
    minimumPickupOrderAmount: 0,
    allowDelivery: false,
    minimumDeliveryOrderAmount: 0,
    hasDeli: false,
    acceptsEBT: false,
    pricesIncludeTax: false,
  };
}

export function NewBusinessDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<NewBusiness>(empty);

  useEffect(() => {
    if (open) setDraft(empty());
  }, [open]);

  const create = useMutation({
    mutationFn: (values: NewBusiness) => post<unknown>('businesses/new', values),
    onSuccess: () => {
      // The new store has to appear in the switcher and in every store picker.
      void queryClient.invalidateQueries({ queryKey: ['businesses'] });
    },
  });

  const set = <K extends keyof NewBusiness>(key: K, value: NewBusiness[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  const emailLooksValid = /^\S+@\S+\.\S+$/.test(draft.ownerEmail.trim());
  const canSave = !!draft.name.trim() && emailLooksValid;

  function submit() {
    create.mutate(draft, {
      onSuccess: () => {
        toast.success(t('newBusiness.created', { name: draft.name }));
        onOpenChange(false);
      },
      onError: (error) =>
        toast.error(
          error instanceof Error ? error.message : t('newBusiness.failed')
        ),
    });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-lg"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>{t('newBusiness.title')}</SheetTitle>
          <p className="text-muted-foreground text-sm">
            {t('newBusiness.subtitle')}
          </p>
        </SheetHeader>

        <div className="space-y-6 p-4">
          <Section title={t('newBusiness.theStore')}>
            <Field
              label={t('newBusiness.ownerEmail')}
              htmlFor="ownerEmail"
              hint={t('newBusiness.ownerEmailHint')}
            >
              <Input
                id="ownerEmail"
                type="email"
                value={draft.ownerEmail}
                disabled={create.isPending}
                onChange={(event) => set('ownerEmail', event.target.value)}
              />
            </Field>
            {draft.ownerEmail.trim() !== '' && !emailLooksValid && (
              <p className="text-destructive text-xs">
                {t('newBusiness.emailInvalid')}
              </p>
            )}

            <Field label={t('newBusiness.name')} htmlFor="name">
              <Input
                id="name"
                value={draft.name}
                disabled={create.isPending}
                onChange={(event) => set('name', event.target.value)}
              />
            </Field>
            <Field label={t('newBusiness.description')} htmlFor="description">
              <Textarea
                id="description"
                rows={2}
                value={draft.description}
                disabled={create.isPending}
                onChange={(event) => set('description', event.target.value)}
              />
            </Field>

            <Toggle
              label={t('newBusiness.isBeta')}
              hint={t('newBusiness.isBetaHint')}
              checked={draft.isBeta}
              disabled={create.isPending}
              onChange={(value) => set('isBeta', value)}
            />
          </Section>

          <Section title={t('newBusiness.where')}>
            <Field label={t('newBusiness.address1')} htmlFor="address1">
              <Input
                id="address1"
                value={draft.address1}
                disabled={create.isPending}
                onChange={(event) => set('address1', event.target.value)}
              />
            </Field>
            <Field label={t('newBusiness.address2')} htmlFor="address2">
              <Input
                id="address2"
                value={draft.address2}
                disabled={create.isPending}
                onChange={(event) => set('address2', event.target.value)}
              />
            </Field>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label={t('newBusiness.city')} htmlFor="city">
                <Input
                  id="city"
                  value={draft.city}
                  disabled={create.isPending}
                  onChange={(event) => set('city', event.target.value)}
                />
              </Field>
              <Field label={t('newBusiness.lat')} htmlFor="lat">
                <Input
                  id="lat"
                  type="number"
                  step="any"
                  value={draft.lat}
                  disabled={create.isPending}
                  onChange={(event) => set('lat', Number(event.target.value))}
                />
              </Field>
              <Field label={t('newBusiness.lon')} htmlFor="lon">
                <Input
                  id="lon"
                  type="number"
                  step="any"
                  value={draft.lon}
                  disabled={create.isPending}
                  onChange={(event) => set('lon', Number(event.target.value))}
                />
              </Field>
            </div>
            <p className="text-muted-foreground text-xs">
              {t('newBusiness.coordinatesHint')}
            </p>
          </Section>

          <Section title={t('newBusiness.fulfilment')}>
            <Toggle
              label={t('newBusiness.allowPickup')}
              checked={draft.allowPickup}
              disabled={create.isPending}
              onChange={(value) => set('allowPickup', value)}
            />
            {/* The minimum only means anything once the channel is on, so it
                appears with it rather than sitting greyed out beside it. */}
            {draft.allowPickup && (
              <Field
                label={t('newBusiness.minimumPickup')}
                htmlFor="minimumPickupOrderAmount"
              >
                <Input
                  id="minimumPickupOrderAmount"
                  type="number"
                  min={0}
                  step="0.01"
                  value={draft.minimumPickupOrderAmount}
                  disabled={create.isPending}
                  onChange={(event) =>
                    set('minimumPickupOrderAmount', Number(event.target.value))
                  }
                />
              </Field>
            )}

            <Toggle
              label={t('newBusiness.allowDelivery')}
              checked={draft.allowDelivery}
              disabled={create.isPending}
              onChange={(value) => set('allowDelivery', value)}
            />
            {draft.allowDelivery && (
              <Field
                label={t('newBusiness.minimumDelivery')}
                htmlFor="minimumDeliveryOrderAmount"
              >
                <Input
                  id="minimumDeliveryOrderAmount"
                  type="number"
                  min={0}
                  step="0.01"
                  value={draft.minimumDeliveryOrderAmount}
                  disabled={create.isPending}
                  onChange={(event) =>
                    set(
                      'minimumDeliveryOrderAmount',
                      Number(event.target.value)
                    )
                  }
                />
              </Field>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <Toggle
                label={t('newBusiness.hasDeli')}
                checked={draft.hasDeli}
                disabled={create.isPending}
                onChange={(value) => set('hasDeli', value)}
              />
              <Toggle
                label={t('newBusiness.acceptsEbt')}
                checked={draft.acceptsEBT}
                disabled={create.isPending}
                onChange={(value) => set('acceptsEBT', value)}
              />
            </div>
            <Toggle
              label={t('newBusiness.pricesIncludeTax')}
              hint={t('newBusiness.pricesIncludeTaxHint')}
              checked={draft.pricesIncludeTax}
              disabled={create.isPending}
              onChange={(value) => set('pricesIncludeTax', value)}
            />
          </Section>

          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="outline"
              disabled={create.isPending}
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button disabled={!canSave || create.isPending} onClick={submit}>
              {create.isPending && <Loader2 className="animate-spin" />}
              {t('newBusiness.create')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
